/**
 * useBulkInvoiceStoragesByFilters - Hook para prefacturar storages usando filtros avanzados
 * 
 * Permite prefacturar múltiples storages basándose en los filtros aplicados mediante
 * POST a /invoices/pre-invoices/storages con el body { advanced_filters: {...} }.
 * Muestra toasts de éxito/error y ejecuta refetch al completar exitosamente.
 * 
 * @returns {Object} { bulkInvoiceByFilters: función de prefacturación, isInvoicing: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { EligibleStorageApiAdapter } from '../adapters/EligibleStorageApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export const useBulkInvoiceStoragesByFilters = () => {
  const { t } = useTranslation('eligibleStorageModule');
  const { toast } = useToast();
  const [isInvoicing, setIsInvoicing] = useState(false);

  const bulkInvoiceByFilters = useCallback(
    async (advancedFilters: { filters: any[]; logic: string }, onSuccess?: () => void) => {
      setIsInvoicing(true);

      try {
        const storagesAdapter = new EligibleStorageApiAdapter(
          createHttpClient(apiClientWH)
        );
        await storagesAdapter.bulkInvoiceStoragesByFilters(advancedFilters);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.bulk_invoice_filters_success.title', 'Storages prefacturados'),
          description: t('toast.bulk_invoice_filters_success.description', 'Los storages se han prefacturado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.bulk_invoice_filters_error.unexpected',
              'Error inesperado al prefacturar los storages. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.bulk_invoice_filters_error.title', 'Error al prefacturar'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsInvoicing(false);
      }
    },
    [t, toast]
  );

  return {
    bulkInvoiceByFilters,
    isInvoicing,
  };
};

